// frontend/src/App.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { supabase } from "./lib/supabaseClient";
import { isEmailSuperAdmin } from "./lib/superAdmin";

// Pages
import LandingPage from "./pages/LandingPage.jsx";
import Dashboard from "./pages/Dashboard.jsx";

import StartAudit from "./pages/StartAudit.jsx";
import StartAuditLegacy from "./pages/StartAuditLegacy.jsx";
import StartAuditProfile from "./pages/StartAuditProfile.jsx";

import UniversalRiskAudit from "./pages/UniversalRiskAudit.jsx";
import UniversalRiskAuditResults from "./pages/UniversalRiskAuditResults.jsx";

import AINews from "./pages/AINews.jsx";
import Reports from "./pages/Reports.jsx";
import RemediationHub from "./pages/RemediationHub.jsx";
import Pricing from "./pages/Pricing.jsx";
import Subscription from "./pages/Subscription.jsx";
import Terms from "./pages/Terms.jsx";
import Legal from "./pages/Legal.jsx";

import Auth from "./pages/Auth.jsx";
import AuthCallback from "./pages/AuthCallback.jsx";
import PaymentSuccess from "./pages/PaymentSuccess.jsx";

import History from "./pages/History.jsx";
import Profile from "./pages/Profile.jsx";
import Sources from "./pages/Sources.jsx";

import AuditAssessment from "./pages/AuditAssessment.jsx";
import AuditAssessmentCopy from "./pages/AuditAssessmentCopy.jsx";
import AuditSnapshot from "./pages/AuditSnapshot.jsx";

import AdminTools from "./pages/AdminTools.jsx";

/**
 * Paywall model:
 * Free (public) routes:
 *  - /
 *  - /universal-audit
 *  - /universal-audit/results
 *  - /pricing
 *  - /subscription
 *  - /terms
 *  - /legal
 *  - /auth
 *  - /auth/callback
 *  - /payment-success
 *
 * Paid-only routes require:
 *  - Supabase session AND
 *  - a PAID subscription row (active OR active_one_time)
 *
 * Capability gating (plans table):
 *  - /reports + /audit-snapshot/* require allow_reports
 *  - /remediation requires allow_remediation
 *
 * Super Admin Dev Tools:
 *  - /admin-tools requires session AND super-admin email allowlist
 *  - (does NOT require paid subscription)
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

const FREE_ROUTES = new Set([
  "/",
  "/universal-audit",
  "/universal-audit/results",
  "/pricing",
  "/subscription",
  "/terms",
  "/legal",
  "/auth",
  "/auth/callback",
  "/payment-success",
]);

function isReportsRoute(pathname) {
  if (!pathname) return false;
  if (pathname === "/reports") return true;
  if (pathname.startsWith("/audit-snapshot/")) return true;
  return false;
}

function isRemediationRoute(pathname) {
  return pathname === "/remediation";
}

function isAdminToolsRoute(pathname) {
  return pathname === "/admin-tools";
}

const PAID_STATUSES = new Set(["active", "active_one_time"]);

function buildNext({ pathname, search, hash }) {
  const next = `${pathname}${search || ""}${hash || ""}`;
  return encodeURIComponent(next);
}

function isRouteFree(pathname) {
  return FREE_ROUTES.has(pathname);
}

function safeBool(x, fallback = false) {
  if (x === true) return true;
  if (x === false) return false;
  return fallback;
}

function safeInt(x, fallback = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function uniqStrings(xs) {
  const out = [];
  const seen = new Set();
  (Array.isArray(xs) ? xs : []).forEach((v) => {
    const s = String(v || "").trim();
    if (!s) return;
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function chooseBestPlan(candidates) {
  const list = Array.isArray(candidates) ? candidates : [];
  if (!list.length) return null;

  const score = (p) => {
    const maxBiz = safeInt(p?.max_businesses, 0) || 0;
    const consultant = safeBool(p?.allow_consultant_workflow, false) ? 1 : 0;
    const remediation = safeBool(p?.allow_remediation, false) ? 1 : 0;
    const reports = safeBool(p?.allow_reports, false) ? 1 : 0;
    const price = safeInt(p?.price_usd_cents, 0) || 0;
    const t = Date.parse(p?.__sub_created_at || "") || 0;
    return [maxBiz, consultant, remediation, reports, price, t];
  };

  let best = list[0];
  let bestScore = score(best);

  for (let i = 1; i < list.length; i += 1) {
    const s = score(list[i]);
    let better = false;
    for (let k = 0; k < s.length; k += 1) {
      if (s[k] > bestScore[k]) {
        better = true;
        break;
      }
      if (s[k] < bestScore[k]) break;
    }
    if (better) {
      best = list[i];
      bestScore = s;
    }
  }

  return best;
}

function PaywallGuard({ children }) {
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search || "";
  const hash = location.hash || "";

  const [ready, setReady] = useState(false);
  const [decision, setDecision] = useState({ allow: false, redirectTo: null });

  // Prevent stale async decide() completions from overwriting newer decisions
  const runIdRef = useRef(0);

  const decide = useCallback(async () => {
    const runId = (runIdRef.current += 1);

    setReady(false);
    setDecision({ allow: false, redirectTo: null });

    // 1) Free routes never require auth/subscription
    if (isRouteFree(pathname)) {
      if (runIdRef.current !== runId) return;
      setDecision({ allow: true, redirectTo: null });
      setReady(true);
      return;
    }

    // 2) Paid-only routes: require session
    let session = null;
    try {
      const { data: sessData } = await supabase.auth.getSession();
      session = sessData?.session || null;
    } catch {
      session = null;
    }

    if (!session?.user?.id) {
      const next = buildNext({ pathname, search, hash });
      if (runIdRef.current !== runId) return;
      setDecision({ allow: false, redirectTo: `/auth?next=${next}` });
      setReady(true);
      return;
    }

    const userId = session.user.id;
    const email = session?.user?.email || "";
    const isSuperAdmin = isEmailSuperAdmin(email);

    // 2B) Super Admin tools route: session + super-admin only (no paid required)
    if (isAdminToolsRoute(pathname)) {
      if (runIdRef.current !== runId) return;
      if (!isSuperAdmin) {
        setDecision({ allow: false, redirectTo: "/dashboard" });
        setReady(true);
        return;
      }
      setDecision({ allow: true, redirectTo: null });
      setReady(true);
      return;
    }

    // 3) Paid check: fetch ALL paid subs for user
    let subs = [];
    try {
      const { data: rows, error: subsErr } = await supabase
        .from("subscriptions")
        .select("status, plan_key, created_at")
        .eq("user_id", userId)
        .in("status", Array.from(PAID_STATUSES))
        .order("created_at", { ascending: false });

      if (subsErr) {
        if (runIdRef.current !== runId) return;
        setDecision({ allow: false, redirectTo: "/pricing" });
        setReady(true);
        return;
      }

      subs = Array.isArray(rows) ? rows : [];
    } catch {
      if (runIdRef.current !== runId) return;
      setDecision({ allow: false, redirectTo: "/pricing" });
      setReady(true);
      return;
    }

    const isPaid = subs.length > 0;
    if (!isPaid) {
      if (runIdRef.current !== runId) return;
      setDecision({ allow: false, redirectTo: "/pricing" });
      setReady(true);
      return;
    }

    const planKeys = uniqStrings(subs.map((r) => r?.plan_key));
    if (!planKeys.length) {
      if (runIdRef.current !== runId) return;
      setDecision({ allow: false, redirectTo: "/pricing" });
      setReady(true);
      return;
    }

    // 4) Fetch plans to decide capabilities
    let plans = [];
    try {
      const { data: planRows, error: planErr } = await supabase
        .from("plans")
        .select(
          "plan_key, max_businesses, allow_reports, allow_remediation, allow_consultant_workflow, audits_per_period, period, price_usd_cents"
        )
        .in("plan_key", planKeys);

      if (planErr) throw planErr;
      plans = Array.isArray(planRows) ? planRows : [];
    } catch {
      if (runIdRef.current !== runId) return;
      setDecision({ allow: false, redirectTo: "/pricing" });
      setReady(true);
      return;
    }

    const merged = subs
      .map((s) => {
        const pk = String(s?.plan_key || "");
        const plan = plans.find((p) => String(p?.plan_key || "") === pk) || null;
        return plan ? { ...plan, __sub_created_at: s?.created_at || null } : null;
      })
      .filter(Boolean);

    const best = chooseBestPlan(merged);

    // Capability gating
    if (isReportsRoute(pathname)) {
      const allowReports = safeBool(best?.allow_reports, false);
      if (!allowReports) {
        if (runIdRef.current !== runId) return;
        setDecision({ allow: false, redirectTo: "/pricing" });
        setReady(true);
        return;
      }
    }

    if (isRemediationRoute(pathname)) {
      const allowRemediation = safeBool(best?.allow_remediation, false);
      if (!allowRemediation) {
        if (runIdRef.current !== runId) return;
        setDecision({ allow: false, redirectTo: "/pricing" });
        setReady(true);
        return;
      }
    }

    if (runIdRef.current !== runId) return;
    setDecision({ allow: true, redirectTo: null });
    setReady(true);
  }, [pathname, search, hash]);

  useEffect(() => {
    decide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (!ready) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 text-slate-900">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold shadow-sm">
          Checking access…
        </div>
      </div>
    );
  }

  if (!decision.allow) {
    return <Navigate to={decision.redirectTo || "/pricing"} replace />;
  }

  return children;
}

export default function App() {
  return (
    <Router>
      <PaywallGuard>
        <Routes>
          <Route path="/" element={<LandingPage />} />

          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/start-audit" element={<StartAudit />} />
          <Route path="/start-audit-legacy" element={<StartAuditLegacy />} />
          <Route path="/start-audit/profile" element={<StartAuditProfile />} />

          <Route path="/universal-audit" element={<UniversalRiskAudit />} />
          <Route path="/universal-audit/results" element={<UniversalRiskAuditResults />} />

          <Route path="/assessment" element={<AuditAssessment />} />
          <Route path="/assessment-copy" element={<AuditAssessmentCopy />} />

          <Route path="/reports" element={<Reports />} />
          <Route path="/audit-snapshot/:snapshotId" element={<AuditSnapshot />} />
          <Route path="/remediation" element={<RemediationHub />} />

          <Route path="/ai-news" element={<AINews />} />

          <Route path="/pricing" element={<Pricing />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/legal" element={<Legal />} />

          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          <Route path="/history" element={<History />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/sources" element={<Sources />} />

          {/* Super Admin */}
          <Route path="/admin-tools" element={<AdminTools />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PaywallGuard>
    </Router>
  );
}